// src/components/AutoAzurInventoryUpdateModule.js
import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse'; // Importa la librería para parsear CSV

// Helper para crear un retraso en la ejecución
const delay = (ms) => new Promise(res => setTimeout(res, ms));

function AutoAzurInventoryUpdateModule() {
  const { token, user } = useAuth();
  const [querySku, setQuerySku] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryMessage, setQueryMessage] = useState('');
  const [csvFile, setCsvFile] = useState(null); // Estado para el archivo CSV
  const [isProcessingCsv, setIsProcessingCsv] = useState(false); // Estado para el procesamiento del CSV
  const [csvMessage, setCsvMessage] = useState(''); // Mensaje para el CSV
  const [inventorySyncResults, setInventorySyncResults] = useState([]);
  const [syncTableLoading, setSyncTableLoading] = useState(true);
  const [syncTableError, setSyncTableError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const itemsPerPage = 15;
  const maxPageButtons = 12;

  // Función para obtener los resultados de sincronización del backend
  const fetchSyncResults = useCallback(async () => {
    setSyncTableLoading(true);
    setSyncTableError(null);
    try {
      const response = await fetch(`/api/autoazur-sync/sync-results?page=${currentPage}&limit=${itemsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      
      if (response.ok || response.status === 304) { // CORRECCIÓN: Manejar 304 Not Modified
        // Solo leer el cuerpo si la respuesta es OK, de lo contrario no hay cuerpo
        if (response.status === 200) {
          const data = await response.json();
          setInventorySyncResults(data.results);
          setTotalItemsCount(data.totalItems);
          setTotalPages(data.totalPages);
        } else {
          // Si es 304, no hay cambios, los datos en el estado siguen siendo válidos
          console.log('[AutoAzurInventoryUpdateModule] Resultados de sincronización no modificados (304).');
        }
      } else {
        const errorData = await response.json();
        setSyncTableError(errorData.mensaje || 'Error al cargar los resultados de sincronización.');
      }
    } catch (err) {
      console.error('[AutoAzurInventoryUpdateModule] Error de red al cargar resultados:', err);
      setSyncTableError('Error de conexión con el servidor al cargar los resultados.');
    } finally {
      setSyncTableLoading(false); // CORRECCIÓN: Desactivar el estado de carga siempre en el finally
    }
  }, [token, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchSyncResults();
  }, [fetchSyncResults]);

  // Manejador para consultar stock de un SKU individual
  const handleQuerySku = async (e) => {
    e.preventDefault();
    setQueryLoading(true);
    setQueryMessage('');
    setInventorySyncResults([]); // Limpiar la tabla de resultados anteriores
    setTotalItemsCount(0);
    setSyncTableError(null); // CORRECCIÓN: Limpiar el error de la tabla al inicio

    if (!querySku.trim()) {
      setQueryMessage('Por favor, ingresa un SKU.');
      setQueryLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/autoazur-sync/sku-warehouse-stock?sku=${encodeURIComponent(querySku)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (response.ok) {
        setQueryMessage(`Consulta exitosa para SKU: ${querySku}`);
        setInventorySyncResults(data.results);
        setTotalItemsCount(data.results.length);
        setTotalPages(1);
      } else {
        setSyncTableError(data.mensaje || 'Error al consultar el SKU.');
      }
    } catch (err) {
      setSyncTableError('Error de conexión con el servidor al consultar el SKU.');
    } finally {
      setQueryLoading(false);
    }
  };

  // Manejador para el cambio de archivo CSV
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      setCsvMessage(`Archivo seleccionado: ${file.name}`);
    } else {
      setCsvFile(null);
      setCsvMessage('Por favor, selecciona un archivo CSV.');
    }
  };

  // NUEVO: Manejador para procesar el CSV y hacer consultas masivas
  const handleBulkQuery = async () => {
    if (!csvFile) {
      setCsvMessage('Por favor, selecciona un archivo CSV primero.');
      return;
    }
    setIsProcessingCsv(true);
    setCsvMessage('Procesando archivo CSV y consultando SKUs...');
    setInventorySyncResults([]);
    setTotalItemsCount(0);
    setSyncTableError(null);

    Papa.parse(csvFile, {
      header: false, // CORREGIDO: Cambiado a 'false' para archivos sin encabezado
      skipEmptyLines: true,
      complete: async (results) => {
        // CORREGIDO: Ahora mapea el primer elemento de cada fila (el SKU)
        const skusToQuery = results.data.map(row => row[0]).filter(Boolean);
        const fetchedResults = [];
        let errors = [];

        for (const sku of skusToQuery) {
          // CORRECCIÓN CLAVE: Esperar 100ms entre cada solicitud para evitar el error "Too Many Requests"
          await delay(100); 
          try {
            const response = await fetch(`/api/autoazur-sync/sku-warehouse-stock?sku=${encodeURIComponent(sku)}`, {
              // CORRECCIÓN CLAVE: Asegurarse de enviar el token con cada solicitud
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
              fetchedResults.push(...data.results);
            } else {
              // Si la respuesta no es OK, añadir un error
              errors.push(`SKU '${sku}': ${data.mensaje || 'Error desconocido.'}`);
            }
          } catch (err) {
            console.error(`Error de red al consultar SKU ${sku}:`, err);
            errors.push(`SKU '${sku}': Error de conexión.`);
          }
        }

        setInventorySyncResults(fetchedResults);
        setTotalItemsCount(fetchedResults.length);
        setTotalPages(1);
        setIsProcessingCsv(false);

        if (errors.length > 0) {
          setCsvMessage(`Procesamiento finalizado con ${errors.length} errores. `);
          setSyncTableError(errors.join('; '));
        } else {
          setCsvMessage(`Procesamiento finalizado. Se encontraron ${fetchedResults.length} resultados.`);
        }
      }
    });
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage, endPage;
    if (totalPages <= maxPageButtons) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxPagesBeforeCurrent = Math.floor(maxPageButtons / 2);
      const maxPagesAfterCurrent = Math.ceil(maxPageButtons / 2) - 1;
      if (currentPage <= maxPagesBeforeCurrent) {
        startPage = 1;
        endPage = maxPageButtons;
      } else if (currentPage + maxPagesAfterCurrent >= totalPages) {
        startPage = totalPages - maxPageButtons + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxPagesBeforeCurrent;
        endPage = currentPage + maxPagesAfterCurrent;
      }
    }
    if (startPage > 1) {
      buttons.push(<button key={1} onClick={() => handlePageChange(1)} className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">1</button>);
      if (startPage > 2) {
        buttons.push(<span key="dots-start" className="px-3 py-1">...</span>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} onClick={() => handlePageChange(i)} className={`px-3 py-1 rounded-lg border ${currentPage === i ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
          {i}
        </button>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="dots-end" className="px-3 py-1">...</span>);
      }
      buttons.push(<button key={totalPages} onClick={() => handlePageChange(totalPages)} className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">{totalPages}</button>);
    }
    return buttons;
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Módulo de Inventario de AutoAzur</h2>

      {/* Sección para buscar un SKU individual */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Consultar SKU Individual</h3>
        <form onSubmit={handleQuerySku} className="flex items-center space-x-4">
          <input
            type="text"
            className="flex-1 shadow-sm border border-gray-300 rounded-lg py-2 px-4 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ingresa SKU para consultar stock"
            value={querySku}
            onChange={(e) => setQuerySku(e.target.value)}
            disabled={queryLoading}
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50"
            disabled={queryLoading}
          >
            {queryLoading ? 'Consultando...' : 'Consultar Stock'}
          </button>
        </form>
        {queryMessage && <p className="text-sm text-green-700 mt-2">{queryMessage}</p>}
      </div>

      {/* NUEVA Sección para subir CSV */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Consultar SKUs por Lotes (CSV)</h3>
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <label htmlFor="csv-upload" className="sr-only">Subir archivo CSV</label>
            <input
              type="file"
              id="csv-upload"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <button
            onClick={handleBulkQuery}
            disabled={!csvFile || isProcessingCsv}
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50"
          >
            {isProcessingCsv ? 'Procesando...' : 'Consultar SKUs de CSV'}
          </button>
        </div>
        {csvMessage && <p className={`text-sm mt-2 ${csvFile ? 'text-green-700' : 'text-red-500'}`}>{csvMessage}</p>}
        <p className="text-xs text-gray-500 mt-2">El archivo debe ser un CSV con la cabecera 'Sku' y los SKUs en la primera columna.</p>
      </div>

      {/* Sección para mostrar los resultados */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Resultados de Sincronización Reciente</h3>
        {syncTableLoading ? (
          <p className="text-center text-gray-600">Cargando resultados de sincronización...</p>
        ) : syncTableError ? (
          <p className="text-center text-red-500">{syncTableError}</p>
        ) : inventorySyncResults.length === 0 ? (
          <p className="text-center text-gray-600">No se encontraron resultados de sincronización o no ha habido sincronización reciente.</p>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Referencia de Pedido</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Título del Producto</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Almacén</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock Actual</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventorySyncResults.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{new Date(item.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.referencia_plataforma}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.sku}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.internal_title}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.almacen}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.current_stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Controles de Paginación */}
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow mt-4">
              <p className="text-sm text-gray-700">
                Mostrando {inventorySyncResults.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} - {Math.min(currentPage * itemsPerPage, totalItemsCount)} de {totalItemsCount} resultados
              </p>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &laquo;
                </button>
                {renderPaginationButtons()}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  &raquo;
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
export default AutoAzurInventoryUpdateModule;
